import React, { FC } from 'react';
import {
  DynamicComponent,
  Errors,
  HeaderText,
  BreadcrumbTree,
} from '@/components';
import { TypeAccountPage } from '@/core/generated-types';
import { HeaderSize } from '@/components/ui/HeaderText';
import {
  ErrorDirectiveProvider,
  useErrorDirectives,
} from '@/context/ErrorDirectiveContext';
import { ERROR_KINDS } from '@/core/constants';
import { findApplicableErrorDetails } from '@/components/common/Errors/utils';

export type ReadingSkillSetStreamingPageLayoutProps = {
  page: TypeAccountPage;
  error?: string | undefined;
};

type ReadingSkillSetStreamingPageChildrenProps = {
  page: TypeAccountPage;
  error?: string | undefined;
};

const ReadingSkillSetStreamingPageChildren: FC<
  ReadingSkillSetStreamingPageChildrenProps
> = ({ page, error }) => {
  const {
    fields: { components },
  } = page;

  const { errorDirectives } = useErrorDirectives();
  const { errors, heading } = findApplicableErrorDetails(
    errorDirectives,
    ERROR_KINDS.COURSE_PLAYER_UNAVAILABLE,
  );
  if (error) {
    const errorsWithServerError = errors.concat(error);
    return <Errors errors={errorsWithServerError} heading={heading} />;
  }

  return (
    <section className="mb-6 lg:mb-10">
      {components &&
        components.map((component) => (
          <DynamicComponent key={component.sys.id} component={component} />
        ))}
    </section>
  );
};

const ReadingSkillSetStreamingPageLayout: FC<
  ReadingSkillSetStreamingPageLayoutProps
> = ({ page, error }) => {
  const {
    fields: { title, breadcrumbTree },
  } = page;

  return (
    <>
      <div className="px-6 md:container mx-auto">
        {breadcrumbTree && (
          <BreadcrumbTree {...breadcrumbTree.fields} classes="mt-0 lg:mt-10" />
        )}
        <HeaderText
          text={title}
          align="left"
          size={HeaderSize.H1}
          classes="mt-5 lg:mt-1 mb-3 lg:mb-10 mx-auto"
        />
      </div>
      <ErrorDirectiveProvider errorDirectives={page.fields.errorDirectives}>
        <ReadingSkillSetStreamingPageChildren page={page} error={error} />
      </ErrorDirectiveProvider>
    </>
  );
};

export default React.memo(ReadingSkillSetStreamingPageLayout);
